/**
 * Client-side photo shrink for Hub Mail — keeps iPhone photos under mail
 * server body limits without a cloud upload. ATW Storage (paid) is the
 * future path for full-resolution / non-image large files.
 *
 * HEIC/HEIF: Chrome (and most non-Safari browsers) cannot decode HEIC in
 * <img>/canvas. Convert via heic2any → JPEG first; if that fails, keep the
 * original bytes so attach never hard-fails.
 */

import type { ComposeAttachment } from "@/mail/composeDraft";

const IMAGE_MIME = /^image\/(jpeg|jpg|png|webp|heic|heif|gif|bmp)$/i;
const IMAGE_EXT = /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i;
const HEIC_MIME = /^image\/(heic|heif)$/i;
const HEIC_EXT = /\.(heic|heif)$/i;

export function isCompressibleImage(file: {
  filename?: string;
  mimeType?: string;
}): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  if (mime.startsWith("image/") && !mime.includes("svg")) return true;
  if (IMAGE_MIME.test(mime)) return true;
  return IMAGE_EXT.test(file.filename || "");
}

export function isHeicImage(file: {
  filename?: string;
  mimeType?: string;
}): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  if (HEIC_MIME.test(mime)) return true;
  return HEIC_EXT.test(file.filename || "");
}

function jpegFilename(name: string): string {
  const base = (name || "photo").replace(/\.[^.]+$/, "");
  return `${base || "photo"}.jpg`;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType || "application/octet-stream" });
}

function blobToBase64(blob: Blob): Promise<{ base64: string; size: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      const base64 = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({ base64, size: blob.size });
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read image bytes."));
    reader.readAsDataURL(blob);
  });
}

/** HEIC convert can hang on some libheif builds — never block attach forever. */
const HEIC_CONVERT_MS = 10_000;

export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** HEIC → JPEG via wasm (works in Chrome / Firefox / Safari). Lazy-loaded + timed. */
async function heicAttachmentToJpeg(
  attachment: ComposeAttachment,
  quality = 0.82,
): Promise<ComposeAttachment> {
  const mod = await withTimeout(
    import("heic2any"),
    HEIC_CONVERT_MS,
    "HEIC converter load timed out.",
  );
  const heic2any = mod.default;
  const blob = base64ToBlob(
    attachment.contentBase64,
    attachment.mimeType || "image/heic",
  );
  // Tip 128: heic2any/libheif can stall at "75%" with no reject — race a timeout.
  const converted = await withTimeout(
    Promise.resolve(
      heic2any({
        blob,
        toType: "image/jpeg",
        quality,
      }),
    ),
    HEIC_CONVERT_MS,
    "HEIC convert timed out.",
  );
  const jpegBlob = Array.isArray(converted) ? converted[0]! : converted;
  if (!jpegBlob) throw new Error("HEIC convert returned empty.");
  const { base64, size } = await blobToBase64(jpegBlob);
  return {
    filename: jpegFilename(attachment.filename),
    mimeType: "image/jpeg",
    contentBase64: base64,
    size,
    compressedFromSize: attachment.compressedFromSize || attachment.size,
  };
}

function loadImageFromBase64(base64: string, mimeType: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image for compression."));
    img.src = `data:${mimeType || "image/jpeg"};base64,${base64}`;
  });
}

function canvasToJpegBase64(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<{ base64: string; size: number }> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not compress image."));
          return;
        }
        void blobToBase64(blob).then(resolve, reject);
      },
      "image/jpeg",
      quality,
    );
  });
}

export type CompressOptions = {
  /** Longest edge in pixels (default 1600). */
  maxEdge?: number;
  /** JPEG quality 0–1 (default 0.72). */
  quality?: number;
  /** Keep retrying lower quality until under this byte size (optional). */
  targetBytes?: number;
};

/**
 * Resize + JPEG-encode a compose attachment. Returns the original if it is
 * not an image, compression does not shrink it, or decode fails (HEIC on
 * browsers without native support is converted first).
 */
export async function compressImageAttachment(
  attachment: ComposeAttachment,
  opts: CompressOptions = {},
): Promise<ComposeAttachment> {
  if (!isCompressibleImage(attachment) || !attachment.contentBase64) {
    return attachment;
  }

  let working = attachment;
  // Tip 125: HEIC never goes through <img> until it is JPEG.
  if (isHeicImage(working)) {
    try {
      working = await heicAttachmentToJpeg(working, opts.quality ?? 0.82);
    } catch {
      // Keep original HEIC so attach still succeeds (recipient may open on iPhone).
      return attachment;
    }
  }

  let maxEdge = opts.maxEdge ?? 1600;
  let quality = opts.quality ?? 0.72;
  const targetBytes = opts.targetBytes ?? 900_000;
  const originalSize = attachment.size || Math.round((attachment.contentBase64.length * 3) / 4);

  let img: HTMLImageElement;
  try {
    img = await loadImageFromBase64(
      working.contentBase64,
      working.mimeType || "image/jpeg",
    );
  } catch {
    // Decode failed (exotic format) — keep whatever we have (JPEG from HEIC or original).
    return working;
  }

  let best: ComposeAttachment | null = null;

  for (let attempt = 0; attempt < 4; attempt++) {
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height, 1));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return working;
    ctx.drawImage(img, 0, 0, width, height);
    try {
      const { base64, size } = await canvasToJpegBase64(canvas, quality);
      const next: ComposeAttachment = {
        filename: jpegFilename(working.filename),
        mimeType: "image/jpeg",
        contentBase64: base64,
        size,
        compressedFromSize: working.compressedFromSize || attachment.size,
      };
      if (!best || (next.size || 0) < (best.size || Infinity)) best = next;
      if ((next.size || 0) <= targetBytes) break;
    } catch {
      break;
    }
    quality = Math.max(0.45, quality - 0.12);
    maxEdge = Math.max(960, Math.round(maxEdge * 0.85));
  }

  if (!best) return working;
  // Prefer compressed only when it is meaningfully smaller than the pre-compress source.
  if ((best.size || 0) >= originalSize * 0.95 && !isHeicImage(attachment)) return working;
  // HEIC → JPEG always preferred for mail interoperability even if similar size.
  return best;
}

export async function compressComposeImages(
  attachments: ComposeAttachment[],
  opts?: CompressOptions,
  onProgress?: (done: number, total: number) => void,
): Promise<{ attachments: ComposeAttachment[]; compressedCount: number; savedBytes: number }> {
  const images = attachments.filter(isCompressibleImage);
  const total = images.length;
  let done = 0;
  let compressedCount = 0;
  let savedBytes = 0;
  const out: ComposeAttachment[] = [];

  for (const file of attachments) {
    if (!isCompressibleImage(file)) {
      out.push(file);
      continue;
    }
    const before = file.size || Math.round((file.contentBase64.length * 3) / 4);
    let next = file;
    try {
      next = await compressImageAttachment(file, opts);
    } catch {
      next = file;
    }
    const after = next.size || before;
    if (after < before || (isHeicImage(file) && next.mimeType === "image/jpeg")) {
      if (after < before) {
        compressedCount += 1;
        savedBytes += before - after;
      } else if (isHeicImage(file) && next.mimeType === "image/jpeg") {
        compressedCount += 1;
      }
    }
    out.push(next);
    done += 1;
    onProgress?.(done, total);
  }

  return { attachments: out, compressedCount, savedBytes };
}
